<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowTopRightOnSquareIcon, StarIcon as StarOutlineIcon } from '@heroicons/vue/24/outline'
import { StarIcon } from '@heroicons/vue/24/solid'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import {
  AREA_TOTAL_META,
  deriveConsolidatedRow,
  type IEnergyCost,
  type IIndicatorCard,
  type ISensorIndicatorResult,
  indicatorMeta,
} from '@/interfaces/entities/management'
import { useManagementStore } from '@/store/management'
import AreaAlarmsSection from './AreaAlarmsSection.vue'
import CostHighlight from './CostHighlight.vue'
import GroupedCards from './GroupedCards.vue'

const store = useManagementStore()
const route = useRoute()

const area = computed(() => store.activeArea)
const indicators = computed(() => (area.value ? store.areaIndicators(area.value.id) : null))
const isFavorite = computed(() => !!area.value && store.favoriteAreaId === area.value.id)

const selectedSensor = computed<ISensorIndicatorResult | null>(() => {
  if (!store.selectedSensorId || !indicators.value) return null
  return indicators.value.sensors.find((s) => s.sensor_id === store.selectedSensorId) ?? null
})

// Rota para a tela de detalhe do sensor (/company/:companyId/sensors/:id).
const sensorDetailRoute = computed(() =>
  selectedSensor.value
    ? { name: 'sensor-detail', params: { companyId: route.params.companyId, id: String(selectedSensor.value.sensor_id) } }
    : null,
)

// Cards de um sensor específico (a partir dos indicadores configurados).
const sensorCards = computed<IIndicatorCard[]>(() => {
  const sensor = selectedSensor.value
  if (!sensor) return []
  return sensor.configured_indicators.map((key) => {
    const meta = indicatorMeta(key)
    return { key, label: meta?.label ?? key, unit: meta?.unit ?? '', value: sensor.values[key] as number | null }
  })
})

// Custo do sensor selecionado (total / ponta / fora ponta).
const sensorCost = computed(() => {
  const custo = selectedSensor.value?.values.custo as IEnergyCost | null | undefined
  if (!custo || typeof custo !== 'object') return null
  return {
    total: custo.cost_total,
    peak: (custo.cost_peak_consumption ?? 0) + (custo.cost_peak_demand ?? 0),
    offpeak: (custo.cost_offpeak_consumption ?? 0) + (custo.cost_offpeak_demand ?? 0),
  }
})

// Custo agregado da área (total / ponta / fora ponta).
const areaCost = computed(() => {
  const totals = indicators.value?.totals
  if (!totals || totals.custo_total === null || totals.custo_total === undefined) return null
  return { total: totals.custo_total, peak: totals.custo_ponta ?? null, offpeak: totals.custo_fora_ponta ?? null }
})

// Totais agregados da área.
const totalCards = computed<IIndicatorCard[]>(() => {
  const totals = indicators.value?.totals
  if (!totals) return []
  return AREA_TOTAL_META.filter((meta) => totals[meta.key] !== null && totals[meta.key] !== undefined).map((meta) => ({
    key: meta.key,
    label: meta.label,
    unit: meta.unit,
    value: totals[meta.key] as number | null,
  }))
})

// KPIs operacionais em destaque (utilização, paradas, tempo parado, demais alarmes).
const ops = computed(() => (indicators.value ? deriveConsolidatedRow(indicators.value) : null))

function formatDuration(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return '—'
  if (minutes < 60) return `${Math.round(minutes)} min`
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins ? `${hours}h ${mins}min` : `${hours}h`
}

function toggleFavorite() {
  if (!area.value) return
  store.setFavoriteArea(isFavorite.value ? null : area.value.id)
}
</script>

<template>
  <div class="flex min-h-[440px] flex-col border border-stroke bg-slate-50 p-4">
    <Loader v-if="store.loadingIndicators && !indicators" />

    <div v-else-if="!area" class="flex flex-1 items-center justify-center text-center text-sm text-slate-400">
      Selecione uma área ou sensor nas colunas para visualizar os indicadores.
    </div>

    <!-- Sensor selecionado -->
    <div v-else-if="selectedSensor" class="space-y-4">
      <header class="flex items-center justify-between gap-2">
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-400">Sensor</p>
          <h2 class="text-lg font-bold text-slate-900">{{ selectedSensor.sensor_name }}</h2>
        </div>
        <router-link
          v-if="sensorDetailRoute"
          :to="sensorDetailRoute"
          class="flex shrink-0 items-center gap-1 border border-primary px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10">
          <ArrowTopRightOnSquareIcon class="h-4 w-4" />
          Ver detalhes do sensor
        </router-link>
      </header>
      <CostHighlight v-if="sensorCost" :total="sensorCost.total" :peak="sensorCost.peak" :offpeak="sensorCost.offpeak" />
      <GroupedCards v-if="sensorCards.length" :cards="sensorCards" />
      <p v-else class="text-sm text-slate-500">Nenhum indicador configurado para este sensor.</p>
    </div>

    <!-- Área selecionada (totais consolidados, agrupados) -->
    <div v-else class="space-y-4">
      <header class="flex items-center justify-between gap-2">
        <div>
          <p class="text-xs uppercase tracking-wide text-slate-400">Área</p>
          <h2 class="text-lg font-bold text-slate-900">{{ area.name }}</h2>
        </div>
        <button
          type="button"
          class="flex shrink-0 items-center gap-1 border px-3 py-1.5 text-xs font-medium transition"
          :class="isFavorite ? 'border-amber-300 bg-amber-50 text-amber-600' : 'border-stroke text-slate-600 hover:bg-slate-50'"
          @click="toggleFavorite">
          <component :is="isFavorite ? StarIcon : StarOutlineIcon" class="h-4 w-4" />
          {{ isFavorite ? 'Área favorita' : 'Definir como favorita' }}
        </button>
      </header>

      <!-- KPIs operacionais em destaque (foco do painel: utilização e paradas) -->
      <div v-if="ops" class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="border border-stroke bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Utilização média</p>
          <p class="mt-0.5 text-3xl font-extrabold text-slate-900">
            {{ ops.utilizacao === null ? '—' : `${ops.utilizacao.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` }}
          </p>
        </div>
        <div class="border border-amber-300 bg-amber-50 px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-amber-700">Tempo total de parada</p>
          <p class="mt-0.5 whitespace-nowrap text-3xl font-extrabold text-amber-700">{{ formatDuration(ops.tempo_parada_min) }}</p>
        </div>
        <div class="border border-stroke bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Qtd de paradas</p>
          <p class="mt-0.5 text-3xl font-extrabold text-slate-900">{{ ops.paradas }}</p>
        </div>
        <div class="border border-stroke bg-white px-4 py-3">
          <p class="text-[11px] font-medium uppercase tracking-wide text-slate-500">Demais alarmes</p>
          <p class="mt-0.5 text-3xl font-extrabold text-slate-900">{{ ops.demais_alarmes }}</p>
        </div>
      </div>

      <!-- Indicadores de alarmes da área (para os gestores) -->
      <AreaAlarmsSection
        v-if="indicators"
        :alarms="indicators.alarms || []"
        :classifications="indicators.alarm_classifications || []" />

      <!-- Energia (consumo/demanda/custo) — secundário neste painel -->
      <GroupedCards v-if="totalCards.length" :cards="totalCards" />
      <p v-else class="text-sm text-slate-500">
        Nenhum indicador disponível. Adicione sensores configurados a esta área.
      </p>

      <CostHighlight v-if="areaCost" :total="areaCost.total" :peak="areaCost.peak" :offpeak="areaCost.offpeak" />
    </div>
  </div>
</template>
